import { createContext, use, useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react'
import { matchPath } from 'react-router'

import { useSession } from '@/shared/auth/session'

/** Which of these records, by public id, the caller may open; one left out may not. */
export type RecordReachLookup = (ids: readonly string[]) => Promise<ReadonlySet<string>>

/** The most ids one lookup is asked for. */
const batchSize = 100

interface ReachStore {
  readonly subscribe: (listener: () => void) => () => void
  readonly get: (id: string) => boolean | undefined
  readonly request: (id: string) => void
}

// What is known of the records the caller may open, asked of `lookup` once per id. Ids asked in the
// same moment, as by every row of a table, share one call per batch.
function reachStore(lookup: RecordReachLookup): ReachStore {
  const known = new Map<string, boolean>()
  const asked = new Set<string>()
  const listeners = new Set<() => void>()
  let queued: string[] = []

  const ask = async (ids: readonly string[]) => {
    try {
      const reached = await lookup(ids)
      for (const id of ids) {
        known.set(id, reached.has(id))
      }
    } catch {
      // Unknown counts as out of reach; a later page asks again.
      for (const id of ids) {
        known.set(id, false)
        asked.delete(id)
      }
    }
    for (const listener of listeners) {
      listener()
    }
  }

  const flush = () => {
    const ids = queued
    queued = []
    for (let start = 0; start < ids.length; start += batchSize) {
      void ask(ids.slice(start, start + batchSize))
    }
  }

  return {
    subscribe: (listener) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    get: (id) => known.get(id),
    request: (id) => {
      if (asked.has(id)) {
        return
      }
      asked.add(id)
      queued.push(id)
      if (queued.length === 1) {
        queueMicrotask(flush)
      }
    },
  }
}

interface Registered {
  readonly route: string
  readonly store: ReachStore
}

const ReachContext = createContext<readonly Registered[]>([])

/**
 * Registers, from a feature's app-wide provider, which of its records the caller may open: those
 * whose page matches `route`, a record route with `:publicId` such as `recordRoute` makes, are
 * linked by {@link RecordLink} only once `lookup` names them. Keep `lookup` the same function, as a
 * new one asks again; a new session does too.
 */
export function RecordReachProvider(props: {
  readonly route: string
  readonly lookup: RecordReachLookup
  readonly children: ReactNode
}) {
  const outer = use(ReachContext)
  const sessionKey = useSession().session?.userProfileId ?? null
  const { route, lookup } = props
  // What one caller may open is not another's, so a new session starts afresh.
  const [current, setCurrent] = useState(() => ({ lookup, sessionKey, store: reachStore(lookup) }))
  if (current.lookup !== lookup || current.sessionKey !== sessionKey) {
    setCurrent({ lookup, sessionKey, store: reachStore(lookup) })
  }
  const { store } = current
  const registered = useMemo(() => [...outer, { route, store }], [outer, route, store])
  return <ReachContext value={registered}>{props.children}</ReachContext>
}

const noStore = (): (() => void) => () => undefined

/**
 * Whether the caller may open the record page `to`: `undefined` until known, and `null` when no
 * feature registered which of those records they may open, or `to` is `null`.
 */
export function useRecordReach(to: string | null): boolean | null | undefined {
  const target = targetOf(use(ReachContext), to)
  const store = target?.store ?? null
  const id = target?.id ?? null

  useEffect(() => {
    if (store !== null && id !== null) {
      store.request(id)
    }
  }, [store, id])

  return useSyncExternalStore(store?.subscribe ?? noStore, () => (store === null || id === null ? null : store.get(id)))
}

// The registered store whose route `to` matches, with the record's public id.
function targetOf(registered: readonly Registered[], to: string | null): { readonly store: ReachStore; readonly id: string } | null {
  if (to === null) {
    return null
  }
  for (const entry of registered) {
    const id = matchPath(entry.route, to)?.params['publicId']
    if (id !== undefined) {
      return { store: entry.store, id }
    }
  }
  return null
}
